/**
 * Schedule editor (§7.2) with a plain-language RRULE preview.
 *
 * Nobody should have to read `FREQ=WEEKLY;BYDAY=MO,WE;BYHOUR=9` to know when
 * their persona will speak, so the sentence is generated as the controls change,
 * not after saving.
 *
 * §7.8 mobile-exempt: on a phone this shows a message rather than a broken grid.
 * Everything safety-critical stays reachable on mobile elsewhere.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import { useLayout } from '../../hooks/useMediaQuery';
import type { Page, Schedule } from '../../api/types';
import { Gate } from '../../components/Gate';
import { Card, Field, Loading, Badge } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { BotTime } from '../../components/TimeDisplay';
import { describeRRule } from '../../lib/rrule';
import { routes } from '../../api/routes';
import { preferredAccount } from '../../lib/accounts';

const DAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export function ScheduleEditor() {
  const { bot, caps } = useBotOutlet();
  const layout = useLayout();
  const list = useApi<Page<Schedule>>(routes.bots.schedules(bot.id));
  const create = useMutation<Record<string, unknown>, Schedule>(routes.bots.schedules(bot.id));
  const remove = useMutation<{ id: string }, unknown>(
    (a) => routes.bots.schedule(bot.id, a.id),
    { method: 'DELETE' },
  );

  const [freq, setFreq] = useState('WEEKLY');
  const [interval, setInterval] = useState(1);
  const [days, setDays] = useState<string[]>(['MO']);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [jitter, setJitter] = useState(300);
  const [body, setBody] = useState('');

  const rrule = [
    `FREQ=${freq}`,
    `INTERVAL=${interval}`,
    freq === 'WEEKLY' && days.length > 0 ? `BYDAY=${days.join(',')}` : '',
    `BYHOUR=${hour}`,
    `BYMINUTE=${minute}`,
  ]
    .filter(Boolean)
    .join(';');

  if (layout === 'compact') {
    return (
      <Card title="Schedule">
        <p className="desktop-only-note">
          The schedule editor needs a wider screen. Open this page on a laptop or desktop to change
          the schedule. Approving a rental, responding to a consent request, re-authorizing an
          account and stopping the bot all work here on a phone.
        </p>
      </Card>
    );
  }

  return (
    <div className="schedule">
      <Card title="Schedules">
        {list.initialLoading ? <Loading label="Loading schedules" /> : null}
        {list.problem ? <ErrorView problem={list.problem} onRetry={list.reload} /> : null}
        {(list.data?.data ?? []).length === 0 && !list.initialLoading ? (
          <EmptyState
            heading="No schedule yet"
            body="Without a schedule the bot only posts when someone composes something."
          />
        ) : (
          <ul className="schedule-list">
            {(list.data?.data ?? []).map((s) => (
              <li key={s.id} className="schedule-item">
                <div>
                  <p>
                    {s.rrule_plain || describeRRule(s.rrule, s.timezone)}{' '}
                    <Badge tone={s.enabled ? 'ok' : 'neutral'}>{s.enabled ? 'on' : 'off'}</Badge>
                  </p>
                  <p className="muted">
                    Next: <BotTime iso={s.next_fire_at} timeZone={s.timezone} /> · jitter{' '}
                    {s.jitter_seconds}s
                  </p>
                  <p className="mono muted">{s.rrule}</p>
                </div>
                <Gate caps={caps} capability="edit_schedule">
                  {(disabled) => (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={disabled || remove.pending}
                      onClick={async () => {
                        await remove.run({ id: s.id });
                        list.reload();
                      }}
                    >
                      Delete
                    </button>
                  )}
                </Gate>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Gate caps={caps} capability="edit_schedule">
        {(disabled) => (
          <Card title="Add a schedule">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const created = await create.run({
                  rrule,
                  timezone: bot.timezone,
                  jitter_seconds: jitter,
                  action_template: { type: 'post', body, social_account_id: preferredAccount(bot.social_accounts)?.id ?? null },
                  enabled: true,
                });
                if (created) {
                  setBody('');
                  list.reload();
                }
              }}
            >
              <fieldset disabled={disabled}>
                <Field id="s-freq" label="How often">
                  <select
                    id="s-freq"
                    className="select"
                    value={freq}
                    onChange={(e) => setFreq(e.target.value)}
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </select>
                </Field>

                <Field id="s-interval" label="Every">
                  <input
                    id="s-interval"
                    className="input input-narrow"
                    type="number"
                    min={1}
                    value={interval}
                    onChange={(e) => setInterval(Number(e.target.value))}
                  />
                </Field>

                {freq === 'WEEKLY' ? (
                  <fieldset className="daypicker">
                    <legend>On these days</legend>
                    {DAYS.map((d) => (
                      <label key={d} className="chip chip-check">
                        <input
                          type="checkbox"
                          checked={days.includes(d)}
                          onChange={() =>
                            setDays((prev) =>
                              prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
                            )
                          }
                        />
                        {d}
                      </label>
                    ))}
                  </fieldset>
                ) : null}

                <div className="row">
                  <Field id="s-hour" label={`Hour (${bot.timezone})`}>
                    <input
                      id="s-hour"
                      className="input input-narrow"
                      type="number"
                      min={0}
                      max={23}
                      value={hour}
                      onChange={(e) => setHour(Number(e.target.value))}
                    />
                  </Field>
                  <Field id="s-min" label="Minute">
                    <input
                      id="s-min"
                      className="input input-narrow"
                      type="number"
                      min={0}
                      max={59}
                      value={minute}
                      onChange={(e) => setMinute(Number(e.target.value))}
                    />
                  </Field>
                  <Field id="s-jitter" label="Jitter (seconds)" hint="Random offset so posts do not look clockwork.">
                    <input
                      id="s-jitter"
                      className="input input-narrow"
                      type="number"
                      min={0}
                      value={jitter}
                      onChange={(e) => setJitter(Number(e.target.value))}
                    />
                  </Field>
                </div>

                <Field id="s-body" label="What to post" hint="Leave empty to generate from the character each time.">
                  <textarea
                    id="s-body"
                    className="textarea"
                    rows={3}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </Field>

                <div className="rrule-preview" aria-live="polite">
                  <strong>In plain words:</strong> {describeRRule(rrule, bot.timezone)}
                  <p className="mono muted">{rrule}</p>
                </div>

                {create.problem ? <InlineError problem={create.problem} /> : null}
                <button type="submit" className="btn btn-primary" disabled={disabled || create.pending}>
                  {create.pending ? 'Saving…' : 'Add this schedule'}
                </button>
              </fieldset>
            </form>
          </Card>
        )}
      </Gate>
    </div>
  );
}
